/*
 * Created Date: Thursday June 23rd 2022
 * Author: Oskar Karnblad
 * License: Proprietary to Scaffcalc AB
 *
 * Copyright (c) 2022 Scaffcalc AB
 */
import ClearIcon from "@mui/icons-material/Clear";
import { ButtonBase, Stack } from "@mui/material";
import {
  BaseSyntheticEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
  FocusEvent,
} from "react";
import validator from "validator";
export interface InputTagProps {
  value: string;
  placeholderValue: string | null;
  unit?: string;
  onChange?: (e: string) => void;
  onConfirm?: () => void;
  onClearDimensionChange?: () => void;
  onFocus?: () => void;
  onBlur?: () => void;
  disabled?: boolean;
  onKeyDown?: (e: React.KeyboardEvent<HTMLSpanElement>) => void;
}

const InputTag = ({
  value,
  placeholderValue,
  unit,
  disabled,
  onChange,
  onConfirm,
  onClearDimensionChange,
  onFocus,
  onBlur,
  onKeyDown,
}: InputTagProps) => {
  const [isFocused, setIsFocused] = useState<boolean>(false);
  const [showCheck, setShowCheck] = useState(true);

  const inputRef = useRef<any>();

  useEffect(() => {
    if (validator.isFloat(value)) inputRef.current.innerText = value;
  }, [value]);

  useEffect(() => {
    if (disabled) {
      setIsFocused(false);
      setShowCheck(false);
    }
  }, [disabled]);

  const handleOnInput = useCallback(
    (e: BaseSyntheticEvent) => {
      if (onChange && !disabled) {
        let targetVal = e.currentTarget.textContent;
        targetVal = targetVal.replace(",", ".");

        if (validator.isFloat(targetVal)) {
          onChange(targetVal);
        } else {
          onChange("");
        }
      }
    },
    [onChange, disabled]
  );

  const handleOnFocus = useCallback(() => {
    setIsFocused(true);

    if (onFocus) onFocus();
  }, [onFocus]);

  const handleOnBlur = useCallback(
    (e: FocusEvent<HTMLSpanElement, Element>) => {
      setIsFocused(false);
      if (e.relatedTarget?.id === "clear-dimension-change") return;
      if (onConfirm && placeholderValue) onConfirm();
      if (onBlur) onBlur();
    },
    [onBlur, onConfirm, placeholderValue]
  );

  const handleOnClearDimensionChange = useCallback(() => {
    if (onClearDimensionChange) {
      if (validator.isFloat(value)) inputRef.current.innerText = value;
      onClearDimensionChange();
    }
  }, [onClearDimensionChange, value]);

  useEffect(() => {
    if (placeholderValue || placeholderValue === "") {
      if (placeholderValue !== value) {
        setShowCheck(true);
      } else {
        setShowCheck(false);
      }
    } else {
      setShowCheck(false);
    }
  }, [value, placeholderValue]);

  return (
    <Stack direction="row" gap="4px">
      <Stack
        direction="row"
        padding="4px 12px"
        gap="2px"
        sx={{
          borderRadius: "16px",
          backgroundColor: isFocused ? "white" : "#70bef3",
          outline: isFocused ? "solid 1px #86c1e8" : "none",
          whiteSpace: "nowrap",
          color: "common.black",
        }}
      >
        <div
          ref={inputRef}
          contentEditable={!disabled}
          onFocus={handleOnFocus}
          onBlur={(e) => handleOnBlur(e)}
          onInput={handleOnInput}
          style={{
            border: "none",
            outline: "none",
            userSelect: disabled ? "none" : "auto",
          }}
          onKeyDown={(e) => {
            if (onKeyDown) onKeyDown(e);
            if (placeholderValue) {
              if (
                !validator.isFloat(placeholderValue + e.key) &&
                ![
                  "Backspace",
                  "ArrowRight",
                  "ArrowLeft",
                  "Delete",
                  ",",
                ].includes(e.key)
              )
                e.preventDefault();

              return;
            } else {
              if (
                !validator.isFloat(e.key) &&
                ![
                  "Backspace",
                  "ArrowRight",
                  "ArrowLeft",
                  "Delete",
                  ",",
                ].includes(e.key)
              )
                e.preventDefault();

              return;
            }
          }}
        />
        <div
          style={{
            userSelect: "none",
          }}
        >
          {unit}
        </div>
      </Stack>
      {showCheck && (
        <ButtonBase
          id="clear-dimension-change"
          onClick={handleOnClearDimensionChange}
          sx={{
            backgroundColor: "white",
            borderRadius: "16px",
            paddingInline: "4px",
          }}
        >
          <ClearIcon
            style={{
              color: "red",
            }}
          />
        </ButtonBase>
      )}
    </Stack>
  );
};

InputTag.defaultProps = {
  value: "",
  placeholderValue: null,
  unit: "m",
  disabled: false,
  onChange: () => {},
};

export default InputTag;
